// ═══ UTILITIES → TAX SAVINGS PACKET (Philip 2026-10-29, packet 2026-10-30 — docs/tax-savings-packet-plan.md) ═══
// Client → property → value year → level of appeal → property type (Real Estate / Business Personal Property, only the
// types the property has) → Parcels, a drop-down checklist of its represented parcels of that type in parcel-number
// order (a parcel with no values at the level or before it starts unticked). The top sheet
// (src/reports/pdf/taxSavingsReportPdf.js, laid out on the Huxley sample) previews here WITH its * / ‡ marks and
// footnotes for the preparer; the printed one in the packet has neither. "Build packet" opens
// src/components/TaxSavingsPacketModal.js: documents → merged PDF → page organizer → Print / Save PDF / Save.
// ⛔ THE FIGURES ARE THE REPORT ENGINE'S (src/reports/taxSavingsReport.js → parcelYearRows): a parcel's Savings equals
//    its Savings in the Property Executive Summary. Nothing here computes a figure.
import React, { useEffect, useRef, useState } from 'react';
import { fetchAll, fetchByParcelIds } from '../reports/pagination';
import { fetchParcelYearScope } from '../reports/parcelYearRows';
import { buildTaxSavingsReport, levelChoices, propertyRows, reportCandidates, reportLevelLabel, PROPERTY_TYPES } from '../reports/taxSavingsReport';
import TaxSavingsPacketModal from '../components/TaxSavingsPacketModal';
import { COMPANY_LOGO, COMPANY_NAME } from '../constants/branding';
import archivo700 from '../assets/fonts/archivo-latin-700-normal.woff';
import publicSans400 from '../assets/fonts/public-sans-latin-400-normal.woff';
import publicSans600 from '../assets/fonts/public-sans-latin-600-normal.woff';
import publicSans700 from '../assets/fonts/public-sans-latin-700-normal.woff';

const FONT = "'Public Sans',system-ui,sans-serif";
const ASSETS = { fonts: { archivo700, publicSans400, publicSans600, publicSans700 }, logo: COMPANY_LOGO, author: COMPANY_NAME };

// The top sheet as a PDF blob: print = true for the packet (no footnotes, no marks), false for the preview.
async function topSheetBlob(report, print) {
  const P = await import('@react-pdf/renderer');
  const { buildTaxSavingsDocument } = await import('../reports/pdf/taxSavingsReportPdf');
  return P.pdf(buildTaxSavingsDocument(P, report, ASSETS, { print })).toBlob();
}

export default function TaxSavingsPacket({ darkMode, staffUser }) {
  const c = darkMode
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const inp = { padding: '6px 9px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '12px', fontFamily: FONT, minWidth: '170px' };
  const lbl = { fontSize: '10px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' };
  const btn = { padding: '6px 12px', background: 'var(--red)', border: '1px solid var(--red)', borderRadius: '7px', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT };

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [years, setYears] = useState([]);
  const [year, setYear] = useState('');
  const [raw, setRaw] = useState(null);          // fetchParcelYearScope for the client and year
  const [levels, setLevels] = useState([]);
  const [level, setLevel] = useState('');
  const [candidates, setCandidates] = useState([]);   // every represented parcel, read up to the level (reportCandidates)
  const [type, setType] = useState('');
  const [included, setIncluded] = useState(null);     // Set of candidate keys ticked
  const [report, setReport] = useState(null);
  const [pdf, setPdf] = useState(null);          // the preview: { url }
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [parcelsOpen, setParcelsOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const parcelsRef = useRef(null);

  const client = clients.find((x) => x.id === clientId) || null;
  const property = properties.find((p) => p.id === propertyId) || null;

  useEffect(() => {
    fetchAll('clients', 'id,name', (q) => q.order('name')).then(setClients, (e) => setErr(`Clients could not be loaded: ${e.message}`));
  }, []);

  useEffect(() => {
    setProperties([]); setPropertyId('');
    if (!clientId) return;
    fetchAll('properties', 'id,client_id,name,address,city,state,postal_code,master_property_id', (q) => q.eq('client_id', clientId).order('name'))
      .then(setProperties, (e) => setErr(`Properties could not be loaded: ${e.message}`));
  }, [clientId]);

  // The years the property's parcels (and parcels with a pro-rata share in it) have values or a status for.
  useEffect(() => {
    setYears([]); setYear('');
    if (!propertyId) return;
    (async () => {
      try {
        const own = await fetchAll('parcels', 'id', (q) => q.eq('property_id', propertyId));
        const shared = await fetchAll('parcel_prorata', 'parcel_id', (q) => q.eq('property_id', propertyId));
        const ids = [...new Set([...own.map((p) => p.id), ...shared.map((p) => p.parcel_id)])];
        const [ap, pyd] = await Promise.all([fetchByParcelIds('parcel_appeals', 'tax_year', ids), fetchByParcelIds('parcel_year_data', 'tax_year', ids)]);
        const ys = [...new Set([...ap, ...pyd].map((r) => String(r.tax_year)).filter((y) => /^\d{4}$/.test(y)))].sort().reverse();
        setYears(ys);
        if (ys.length) setYear(ys[0]);
      } catch (e) { setErr(`Years could not be loaded: ${e.message}`); }
    })();
  }, [propertyId]);

  // One fetch per client and year (the engine's scope); a level change rebuilds from it.
  useEffect(() => {
    setRaw(null); setLevels([]); setLevel('');
    if (!clientId || !propertyId || !year) return;
    let alive = true;
    setBusy('Loading figures…'); setErr('');
    fetchParcelYearScope({ clientIds: [clientId], years: [year] }, 'tax savings report').then((r) => {
      if (!alive) return;
      setRaw(r);
      const ch = levelChoices(r ? propertyRows(r, { propertyId, level: null }).taxable : []);
      setLevels(ch.levels.length ? ch.levels : ['proposed']);
      setLevel(ch.defaultLevel);
    }, (e) => { if (alive) setErr(`Figures could not be loaded: ${e.message}`); }).finally(() => { if (alive) setBusy(''); });
    return () => { alive = false; };
  }, [clientId, propertyId, year]);

  // The level decides each parcel's figures and whether it has values; the types offered are the ones it has.
  useEffect(() => {
    const cs = raw && level ? reportCandidates(raw, { propertyId, year, level }) : [];
    setCandidates(cs);
    const types = PROPERTY_TYPES.filter((t) => cs.some((r) => r.type === t.key));
    setType((cur) => (types.some((t) => t.key === cur) ? cur : types[0]?.key || ''));
  }, [raw, level]);

  // A new level or type starts with every parcel that has values ticked.
  useEffect(() => {
    setIncluded(new Set(candidates.filter((r) => r.type === type && r.hasValues).map((r) => r.key)));
  }, [candidates, type]);

  useEffect(() => {
    setReport(raw && property && level && type && included?.size ? buildTaxSavingsReport(raw, { client, property, year, level, type, included }) : null);
  }, [included]);

  const typeRows = candidates.filter((r) => r.type === type);
  const toggle = (key) => setIncluded((cur) => { const n = new Set(cur); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  // The parcels drop-down closes on a click outside it.
  useEffect(() => {
    if (!parcelsOpen) return undefined;
    const onDown = (e) => { if (!parcelsRef.current?.contains(e.target)) setParcelsOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [parcelsOpen]);

  // The preview, rendered once per report, with the marks and footnotes the printed top sheet leaves out.
  useEffect(() => {
    let alive = true, url = null;
    setPdf(null);
    if (!report) return undefined;
    topSheetBlob(report, false).then((blob) => {
      if (!alive) return;
      url = URL.createObjectURL(blob);
      setPdf({ url });
    }, (e) => { if (alive) setErr(`The PDF could not be built: ${e?.message || e}`); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [report]);

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: c.text }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>Tax Savings Packet</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>One property, one value year, one level of appeal — the top sheet and its supporting documents in one PDF.</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '14px' }}>
        <div><div style={lbl}>Client</div>
          <select style={inp} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Choose…</option>
            {clients.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></div>
        <div><div style={lbl}>Property</div>
          <select style={{ ...inp, minWidth: '260px' }} value={propertyId} onChange={(e) => setPropertyId(e.target.value)} disabled={!properties.length}>
            <option value="">{clientId && !properties.length ? 'No properties' : 'Choose…'}</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div><div style={lbl}>Value year</div>
          <select style={{ ...inp, minWidth: '90px' }} value={year} onChange={(e) => setYear(e.target.value)} disabled={!years.length}>
            {!years.length && <option value="">{propertyId ? 'No years' : '—'}</option>}
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></div>
        <div><div style={lbl}>Level of appeal</div>
          <select style={{ ...inp, minWidth: '120px' }} value={level} onChange={(e) => setLevel(e.target.value)} disabled={!levels.length}>
            {!levels.length && <option value="">—</option>}
            {levels.map((l) => <option key={l} value={l}>{reportLevelLabel(l)}</option>)}
          </select></div>
        <div><div style={lbl}>Property type</div>
          <select style={{ ...inp, minWidth: '200px' }} value={type} onChange={(e) => setType(e.target.value)} disabled={!candidates.length}>
            {!candidates.length && <option value="">—</option>}
            {PROPERTY_TYPES.filter((t) => candidates.some((r) => r.type === t.key)).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select></div>
        <div ref={parcelsRef} style={{ position: 'relative' }}><div style={lbl}>Parcels</div>
          <button type="button" style={{ ...inp, minWidth: '150px', textAlign: 'left', cursor: typeRows.length ? 'pointer' : 'default' }} disabled={!typeRows.length}
            onClick={() => setParcelsOpen((v) => !v)}>
            {typeRows.length && included ? `${included.size} of ${typeRows.length}` : '—'} <span style={{ float: 'right', fontSize: '10px' }}>▾</span>
          </button>
          {parcelsOpen && included && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: '4px', minWidth: '260px', maxHeight: '320px', overflowY: 'auto',
              background: darkMode ? '#232b40' : '#fff', border: `1px solid ${c.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: '6px 10px' }}>
              {typeRows.map((r) => (
                <label key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', padding: '4px 0' }}>
                  <input type="checkbox" checked={included.has(r.key)} onChange={() => toggle(r.key)} />
                  <span style={{ flex: 1 }}>{r.parcel}{r.pro_rata_pct != null ? ` (${r.pro_rata_pct}%)` : ''}</span>
                  {!r.hasValues && <span style={{ color: c.muted, fontStyle: 'italic' }}>no values</span>}
                </label>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <button style={report && pdf ? btn : { ...btn, opacity: 0.5, cursor: 'default' }} disabled={!report || !pdf} onClick={() => setPacketOpen(true)}>Build packet</button>
      </div>

      {err && <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'rgba(180,0,0,0.08)', color: 'var(--red)', fontSize: '13px', marginBottom: '12px' }}>{err}</div>}
      {busy && <div style={{ fontSize: '13px', color: c.muted, marginBottom: '12px' }}>{busy}</div>}

      {pdf && <div style={{ fontSize: '11px', color: c.muted, marginBottom: '6px' }}>Preview — the * / ‡ marks and the notes at the bottom are for you; the printed top sheet leaves them out.</div>}
      {pdf
        ? <iframe title="Tax Savings Packet top sheet preview" src={pdf.url} style={{ width: '100%', height: '78vh', border: `1px solid ${c.border}`, borderRadius: '8px', background: '#fff' }} />
        : !busy && <div style={{ padding: '40px', textAlign: 'center', color: c.muted, fontSize: '13px', border: `1px dashed ${c.border}`, borderRadius: '12px' }}>
          {report ? 'Building the PDF…' : typeRows.length ? 'Tick at least one parcel.' : 'Choose a client, property and value year.'}
        </div>}

      {packetOpen && report && (
        <TaxSavingsPacketModal darkMode={darkMode} staffUser={staffUser} property={property} report={report} rows={report.rows} level={level}
          typeLabel={PROPERTY_TYPES.find((t) => t.key === type)?.label || ''} topSheet={() => topSheetBlob(report, true)} onClose={() => setPacketOpen(false)} />
      )}
    </div>
  );
}
